import React, { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Table,
  Text,
} from "@mantine/core";
import { DATA_CURRENCY, DATA_ORDER_TYPE, DATA_PAY_MODES } from "../data/options";
import { useServerApi } from "../hooks/userServerApi";
import { useFormatter } from "../hooks/useFomatter";

const DATA_LINE_OF_BUSINESS = [
  { value: "LIFE", label: "LIFE" },
  { value: "GI", label: "GI" },
  { value: "MPF", label: "MPF" },
  { value: "INV", label: "INV" },
];

const SKIP_REASON_LABELS = {
  already_accrued: "Already accrued",
  cool_offed: "Order status is COL",
  missing_cooling_off: "Missing cooling-off date",
  cooling_off_not_passed: "Cooling-off has not passed",
  filter_mismatch: "Sample order does not match the point type filters",
  missing_year_premium: "Missing or zero premium",
  invalid_fx_rate: "Missing exchange rate for the order or base currency",
  missing_pay_years: "Pay years is required",
  no_band_match: "No pay-year band matches this term",
};

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatRate = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";
  return amount.toLocaleString(undefined, { maximumFractionDigits: 8 });
};

const emptySample = {
  premium: 10000,
  currency: "USD",
  payMode: "ANNUALLY",
  paymentTerm: 10,
  consultantCount: 2,
  lineOfBusiness: "LIFE",
  type: "NEW",
  isRider: false,
};

const PointTypeTester = ({ form }) => {
  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const [sample, setSample] = useState(emptySample);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);

  const updateSample = (field, value) => {
    setSample((current) => ({ ...current, [field]: value }));
  };

  const handleCalculate = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.previewPointType({
        pointType: form?.values || {},
        order: {
          premium: sample.premium,
          currency: sample.currency,
          payMode: sample.payMode,
          paymentTerm: sample.paymentTerm,
          consultantCount: sample.consultantCount,
          lineOfBusiness: sample.lineOfBusiness || undefined,
          type: sample.type || undefined,
          isRider: sample.isRider,
        },
      });
      setPlan(result);
    } catch (err) {
      setPlan(null);
      setError(err?.message || "Failed to preview points");
    } finally {
      setLoading(false);
    }
  };

  const snapshot = plan?.snapshot || {};

  return (
    <>
      <Text size="sm" color="dimmed" mb="md">
        Score a sample order against the current Setting values. Nothing is
        posted to the ledger.
      </Text>
      <SimpleGrid cols={2} spacing="md" mb="md">
        <NumberInput
          label="Modal Premium"
          description="Installment premium in the selected currency. Year premium = this × pay mode (monthly × 12, quarterly × 4)."
          value={sample.premium}
          min={0}
          precision={2}
          parser={formatter.numberParser}
          formatter={formatter.numberFormat}
          onChange={(value) => updateSample("premium", value)}
        />
        <Select
          label="Currency"
          data={DATA_CURRENCY}
          value={sample.currency}
          searchable
          onChange={(value) => updateSample("currency", value || "USD")}
        />
        <Select
          label="Pay Mode"
          data={DATA_PAY_MODES}
          value={sample.payMode}
          onChange={(value) => updateSample("payMode", value || "ANNUALLY")}
        />
        <NumberInput
          label="Pay Years"
          value={sample.paymentTerm}
          min={1}
          onChange={(value) => updateSample("paymentTerm", value)}
        />
        <NumberInput
          label="Consultants"
          value={sample.consultantCount}
          min={1}
          onChange={(value) => updateSample("consultantCount", value)}
        />
        <Select
          label="Line of Business"
          data={DATA_LINE_OF_BUSINESS}
          value={sample.lineOfBusiness}
          placeholder="Match Setting"
          clearable
          onChange={(value) => updateSample("lineOfBusiness", value)}
        />
        <Select
          label="Order Type"
          data={DATA_ORDER_TYPE}
          value={sample.type}
          placeholder="Match Setting"
          clearable
          onChange={(value) => updateSample("type", value)}
        />
        <Checkbox
          label="Rider"
          mt="xl"
          checked={Boolean(sample.isRider)}
          onChange={(event) =>
            updateSample("isRider", event.currentTarget.checked)
          }
        />
      </SimpleGrid>
      <Group mb={40}>
        <Button onClick={handleCalculate} loading={loading}>
          Calculate
        </Button>
      </Group>

      {error && (
        <Alert color="red" variant="light" mb="md">
          {error}
        </Alert>
      )}

      {plan && !plan.eligible && (
        <Alert color="yellow" variant="light">
          {SKIP_REASON_LABELS[plan.skipReason] || plan.skipReason || "Not eligible"}
        </Alert>
      )}

      {plan?.eligible && (
        <>
          <Text size="xl" weight={700} mb="xs" color="green">
            {form?.values?.name || form?.values?.code || "Points"} :{" "}
            {formatAmount(plan.orderPoints)}
          </Text>
          <Table fontSize="sm" withBorder mb="md">
            <tbody>
              <tr>
                <td>Year premium ({snapshot.orderCurrency || "-"})</td>
                <td>{formatAmount(snapshot.yearPremiumOrderCurrency)}</td>
              </tr>
              <tr>
                <td>FX rate</td>
                <td>{formatRate(snapshot.fxRate)}</td>
              </tr>
              <tr>
                <td>Year premium ({snapshot.baseCurrency || "-"})</td>
                <td>{formatAmount(snapshot.yearPremiumBase)}</td>
              </tr>
              <tr>
                <td>Pay years</td>
                <td>{snapshot.payYears ?? "-"}</td>
              </tr>
              <tr>
                <td>Multiplier</td>
                <td>{snapshot.multiplier ?? "-"}</td>
              </tr>
              <tr>
                <td>
                  <strong>Order points</strong>
                </td>
                <td>
                  <strong>{formatAmount(plan.orderPoints)}</strong>
                </td>
              </tr>
            </tbody>
          </Table>
          <Text size="sm" weight={500} mb={4}>
            Consultant shares
          </Text>
          <Table fontSize="sm" withBorder>
            <thead>
              <tr>
                <th>Consultant</th>
                <th>Points</th>
              </tr>
            </thead>
            <tbody>
              {(plan.consultantShares || []).map((share, index) => (
                <tr key={share.userId || index}>
                  <td>Consultant {index + 1}</td>
                  <td>{formatAmount(share.amount)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </>
  );
};

export default PointTypeTester;
